import React from 'react'
import { css } from '@emotion/core'
import { HintCategoryIcon, type HintCategory } from '@fs/zion-research-help'
import MyHeritageIcon from '../../images/MyHeritageIcon.svg'

// Matches the 18px of an xs HintCategoryIcon so the two badges sit identically in a tab label.
const myHeritageCss = css`
  display: inline-flex;

  img {
    width: 18px;
    height: 18px;
  }
`

type Props = {
  category: HintCategory | undefined
}

/**
 * The badge standing for the highest-priority hint a person has. Renders nothing when the person
 * has no hints, so callers can hand it the category straight from usePersonHintSummary.
 *
 * The icon and color for every category live in `@fs/zion-research-help` alongside the person-banner
 * indicators, so the two can never drift. MyHeritage is the exception: it is a partner brand mark
 * rather than a Zion icon, so that one is drawn here.
 *
 * No aria-label: the surrounding Tab already names the hint through HINTS_TAB's getAriaLabel, and a
 * label here would make a screen reader announce the category twice.
 */
export default function HintIcon({ category }: Props): JSX.Element | null {
  if (!category) return null

  if (category === 'myheritage-matches') {
    return (
      <span css={myHeritageCss} data-testid="hint-icon-myheritage-matches">
        {/* Decorative: HINTS_TAB's getAriaLabel names the category on the tab itself. */}
        <img src={MyHeritageIcon} alt="" />
      </span>
    )
  }

  return (
    <span data-testid={`hint-icon-${category}`}>
      <HintCategoryIcon category={category} />
    </span>
  )
}
